import { Box, Button, Card, Flex, Stack } from '@mantine/core';
import {
  IconArrowsVertical,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconList,
  IconPencil,
  IconUser,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { getTextSelectionEditPrompt } from '../constants/GeneratedContent';

const TextSelectionEditor = ({
  textSelectionEditorPosition,
  handleTextSelectionEditorOpen,
  selectedText,
  onTextEdited,
  aiModel,
  provider,
  setError,
  controllerRef,
  setShowAbortButton,
}) => {
  const { domain } = useSelector((state) => state.domain);
  const brandTones = domain?.brandContext?.tones?.value || [];
  const [toneOptionsOpen, setToneOptionsOpen] = useState(false);
  const [selectedTone, setSelectedTone] = useState('');
  const [selectedEditCommand, setSelectedEditCommand] = useState();

  const submitEditRequest = async () => {
    handleTextSelectionEditorOpen(false);
    const textEditPrompt = getTextSelectionEditPrompt(selectedText, selectedEditCommand, 'en', selectedTone);
    const requestUrl = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/prompt/stream`;
    const requestBody = {
      prompt: textEditPrompt,
      model: aiModel,
      provider: provider,
      isAiHub: true,
    };

    setError(false);

    try {
      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      setShowAbortButton(true);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal,
      });

      if (response.ok) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let result;
        while (!result?.done) {
          result = await reader.read();
          let chunk = decoder.decode(result.value);
          onTextEdited(chunk);
        }
      } else {
        setError({ message: `Error: ${response.statusText}` });
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        setError(error);
      }
    } finally {
      setShowAbortButton(false);
    }
  };

  const handleOptionClick = (option) => {
    setSelectedEditCommand(option);
    if (option !== 'changeTone') {
      setToneOptionsOpen(false);
      setSelectedTone('');
    }
  };

  const toggleToneOptions = () => {
    setToneOptionsOpen(!toneOptionsOpen);
    if (!toneOptionsOpen) {
      setSelectedEditCommand('changeTone');
    } else {
      setSelectedEditCommand('');
    }
  };

  const copyText = (text) => {
    if (text && text !== '' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      handleTextSelectionEditorOpen(false);
    }
  };
  return (
    <Card
      withBorder
      style={{
        position: 'absolute',
        top: textSelectionEditorPosition.top,
        left: textSelectionEditorPosition.left,
        padding: 0,
        height: 'auto',
        boxShadow: '0px 2px 4px #7936864d',
      }}
      p="sm"
    >
      <Stack gap="xs">
        <button
          className={`button-element ${
            selectedEditCommand === 'summarize' ? 'active' : ''
          } no-border`}
          style={{ width: '100%', color: 'var(--mantine-primary-color-filled)' }}
          onClick={() => handleOptionClick('summarize')}
        >
          <Box component={IconList} size={16} mr="sm" style={{ display: 'inline' }} />
          <span style={{ flexGrow: 1, fontSize: '15px', padding: 0, margin: 0 }}>
            Summarize
          </span>
        </button>

        <button
          className={`button-element ${selectedEditCommand === 'longer' ? 'active' : ''} no-border`}
          style={{ width: '100%', color: 'var(--mantine-primary-color-filled)' }}
          onClick={() => handleOptionClick('longer')}
        >
          <Box component={IconArrowsVertical} size={16} mr="sm" style={{ display: 'inline', transform: 'scaleY(-1)' }} />
          <span style={{ flexGrow: 1, fontSize: '15px', padding: 0, margin: 0 }}>
            Make Longer
          </span>
        </button>

        <button
          className={`button-element ${selectedEditCommand === 'shorter' ? 'active' : ''} no-border`}
          style={{ width: '100%', color: 'var(--mantine-primary-color-filled)' }}
          onClick={() => handleOptionClick('shorter')}
        >
          <Box component={IconArrowsVertical} size={16} mr="sm" style={{ display: 'inline' }} />
          <span style={{ flexGrow: 1, fontSize: '15px', padding: 0, margin: 0 }}>
            Make Shorter
          </span>
        </button>

        <Flex direction="column" align="center" w="100%">
          <button
            className={`button-element ${toneOptionsOpen || selectedTone ? 'active' : ''} no-border`}
            style={{ width: '100%', color: 'var(--mantine-primary-color-filled)' }}
            onClick={toggleToneOptions}
            disabled={brandTones.length === 0}
          >
            <Box component={IconUser} size={16} mr="sm" style={{ display: 'inline' }} />
            <span style={{ flexGrow: 1, fontSize: '15px', padding: 0, margin: 0 }}>
              Change Tone
            </span>
            {toneOptionsOpen ? (
              <Box component={IconChevronUp} size={14} pl="sm" style={{ display: 'inline' }} />
            ) : (
              <Box component={IconChevronDown} size={14} pl="sm" style={{ display: 'inline' }} />
            )}
          </button>
          {toneOptionsOpen && (
            <Flex direction="column" className="button-element" style={{ boxShadow: '1px' }}>
              {brandTones.map((tone, index) => (
                <button
                  key={index}
                  className={`button-element ${selectedTone === tone ? 'active' : ''} no-border`}
                  style={{ width: '100%', color: 'var(--mantine-primary-color-filled)' }}
                  onClick={() => {
                    setSelectedTone(tone);
                    handleOptionClick('changeTone');
                  }}
                >
                  {tone}
                </button>
              ))}
            </Flex>
          )}
        </Flex>

        <Flex mt="sm" align="center" w="100%">
          <Button fullWidth onClick={() => submitEditRequest()} disabled={!selectedEditCommand}>
            <Box component={IconPencil} size={14} pr="xs" style={{ display: 'inline' }} />
            Edit
          </Button>
        </Flex>
        <Flex align="center" w="100%">
          <Button fullWidth onClick={() => copyText(selectedText)}>
            <Box component={IconCopy} size={14} pr="xs" style={{ display: 'inline' }} />
            Copy
          </Button>
        </Flex>
      </Stack>
    </Card>
  );
};

export default TextSelectionEditor;
